import type { CellData, Column, RowData, TableFeatures } from '@tanstack/react-table';
import {
	column_getCanSort,
	column_getIsSorted,
	column_getToggleSortingHandler,
} from '@tanstack/react-table/static-functions';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { t } from '@/i18n';
import { cn } from '@/lib/utils';

type DataTableColumnHeaderProps<TFeatures extends TableFeatures, TData extends RowData, TValue extends CellData> = {
	column: Column<TFeatures, TData, TValue>;
	title: string;
	className?: string;
};

/** Column header that toggles sorting (asc → desc → none) when the column is sortable. */
export function DataTableColumnHeader<TFeatures extends TableFeatures, TData extends RowData, TValue extends CellData>({
	column,
	title,
	className,
}: DataTableColumnHeaderProps<TFeatures, TData, TValue>) {
	if (!column_getCanSort(column)) {
		return <span className={className}>{title}</span>;
	}

	const sorted = column_getIsSorted(column);
	const SortIcon = sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ArrowUpDown;

	return (
		<Button
			variant="ghost"
			size="sm"
			className={cn(
				'-ml-2 h-8 px-2 font-semibold text-muted-foreground hover:text-foreground cursor-pointer',
				className,
			)}
			onClick={column_getToggleSortingHandler(column)}
			aria-label={t('common.sort.byColumn', { column: title })}
		>
			{title}
			<SortIcon className={cn('size-3.5', !sorted && 'opacity-50')} />
		</Button>
	);
}
